import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useUpdateDealSetupNote } from '@/lib/hooks/useUpdateDealSetupNote';
import Button from '@appchoose/button';
import { AlertCircle, Info } from 'lucide-react';

export function SetupNoteEditor({
  value,
  onChange,
  disabled,
  readOnly,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Hides the instruction template; textarea is non-editable. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const isReadOnly = Boolean(readOnly);
  const isDisabled = Boolean(disabled) || isReadOnly;
  return (
    <div className="space-y-4">
      {!isReadOnly ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
          <p className="mb-3 font-medium text-slate-800">
            {t('components.setup_note_modal.template_intro')}
          </p>
          <ul className="space-y-2.5">
            <li className="flex gap-2">
              <span className="shrink-0" aria-hidden="true">
                🎁
              </span>
              <span>
                <strong className="font-medium text-slate-800">
                  {t('components.setup_note_modal.template_gwp_label')}
                </strong>
                {' — '}
                <span className="text-slate-600">
                  {t('components.setup_note_modal.template_gwp_example')}
                </span>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="shrink-0" aria-hidden="true">
                ⚖️
              </span>
              <span>
                <strong className="font-medium text-slate-800">
                  {t('components.setup_note_modal.template_retours_label')}
                </strong>
                {' — '}
                <span className="text-slate-600">
                  {t('components.setup_note_modal.template_retours_example')}
                </span>
              </span>
            </li>
          </ul>
        </div>
      ) : null}
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('components.setup_note_modal.template_placeholder')}
        className="min-h-[200px] resize-none border border-orange-500 bg-orange-50 focus-visible:border-2 focus-visible:border-orange-500 focus-visible:ring-0"
        disabled={isDisabled}
      />
    </div>
  );
}

interface SetupNoteModalProps {
  dealId: string;
  currentNote: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNoteUpdate?: (note: string) => void;
  /** When true: note is visible but not editable; save is hidden. */
  readOnly?: boolean;
  /** Shown under the title when `readOnly` (e.g. why editing is blocked). */
  readOnlyHint?: string | null;
  /**
   * Visual style for the notice when read-only (aligned with shipping-policy locked banner vs locked-offer infobox).
   */
  readOnlyNoticeVariant?: 'editing_locked' | 'negotiation_closed' | null;
}

function SetupNoteModal({
  dealId,
  currentNote,
  open,
  onOpenChange,
  onNoteUpdate,
  readOnly = false,
  readOnlyHint,
  readOnlyNoticeVariant = null,
}: SetupNoteModalProps) {
  const { t } = useTranslation();
  const [note, setNote] = useState('');
  const { updateSetupNote, loading } = useUpdateDealSetupNote(dealId);
  const { toast } = useToast();

  useEffect(() => {
    setNote(currentNote || '');
  }, [currentNote]);

  const handleSave = async () => {
    try {
      const result = await updateSetupNote(note);
      if (result.success) {
        toast({
          title: t('components.setup_note_modal.note_saved'),
          description: t('components.setup_note_modal.note_saved_success'),
        });
        onNoteUpdate?.(note);
        onOpenChange(false);
      } else {
        toast({
          title: t('general.error'),
          description: t('components.setup_note_modal.note_save_error'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la note:', error);
      toast({
        title: t('general.error_network'),
        description: t('general.error_network_message'),
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    if (!loading) {
      setNote(currentNote || '');
      onOpenChange(false);
    }
  };

  const hasChanges = note !== (currentNote || '');

  const readOnlyDescriptionContent =
    readOnlyHint ?? t('components.setup_note_modal.description_view_only');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 sm:max-w-[600px]">
        <div className="min-h-0 flex-1 overflow-y-auto">
          <DialogHeader className="space-y-3">
            <DialogTitle>{t('components.setup_note_modal.title')}</DialogTitle>
            {!readOnly ? (
              <DialogDescription asChild>
                <p className="text-slate-800">
                  {t('components.setup_note_modal.description')}
                </p>
              </DialogDescription>
            ) : null}
            {readOnly && readOnlyNoticeVariant === 'editing_locked' ? (
              <DialogDescription asChild>
                <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  <AlertCircle
                    className="mt-0.5 h-5 w-5 shrink-0"
                    aria-hidden
                  />
                  <p>{readOnlyDescriptionContent}</p>
                </div>
              </DialogDescription>
            ) : null}
            {readOnly && readOnlyNoticeVariant === 'negotiation_closed' ? (
              <DialogDescription asChild>
                <div className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                  <Info
                    className="mt-0.5 h-4 w-4 shrink-0 text-blue-600"
                    aria-hidden
                  />
                  <p>{readOnlyDescriptionContent}</p>
                </div>
              </DialogDescription>
            ) : null}
            {readOnly && !readOnlyNoticeVariant ? (
              <DialogDescription asChild>
                <p className="text-slate-800">{readOnlyDescriptionContent}</p>
              </DialogDescription>
            ) : null}
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <SetupNoteEditor
              value={note}
              onChange={setNote}
              disabled={loading}
              readOnly={readOnly}
            />
          </div>
        </div>
        <DialogFooter className="shrink-0 border-t pt-4">
          {readOnly ? (
            <Button
              type="button"
              appearance="primary"
              onClick={handleClose}
              disabled={loading}
            >
              {t('general.close')}
            </Button>
          ) : (
            <>
              <Button
                type="button"
                appearance="outline"
                onClick={handleClose}
                disabled={loading}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type="button"
                appearance="primary"
                onClick={handleSave}
                disabled={loading || !hasChanges}
              >
                {loading ? t('general.saving') : t('general.save')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SetupNoteModal;
